// © Microsoft Corporation. All rights reserved.
import React, { useState, useEffect } from 'react';
import GroupCall from './containers/GroupCall';
import { createStore, applyMiddleware } from 'redux';
import { Provider } from 'react-redux';
import { reducer } from './core/reducers';
import thunk from 'redux-thunk';
import EndCall from './components/EndCall';
import NotJoined from './components/NotJoined';
import HomeScreen from './components/HomeScreen';
import ConfigurationScreen from './containers/Configuration';
import { v1 as createGUID } from 'uuid';
import { loadTheme, initializeIcons, Spinner } from '@fluentui/react';
import { utils } from './Utils/Utils';
import { CallEndReason, VideoDeviceInfo, AudioDeviceInfo } from '@azure/communication-calling';
import { GetInterviewDetails, CheckIsMobile } from 'Utils/participantInfo'


const sdkVersion = require('../package.json').dependencies['@azure/communication-calling'];
const lastUpdated = `Last Updated ${utils.getBuildTime()} with @azure/communication-calling:${sdkVersion}`;


console.log("COGBEE :: sdkVersion ");
console.log(sdkVersion);
console.log("lastUpdated");
console.log(lastUpdated);
declare global {
    var isCameraOn: string;
    var isMicOn: string;
    var vdInfo: VideoDeviceInfo|null;
    var adInfo: AudioDeviceInfo | null;
    var answerAPI: string;
    var rAPI: string;
    var coAPI: string;
    var setRecordingOn: number;
    var currentTimestamp: number;
    var currentUTCTime: Date;
    var _callAgent: any;
    var tokenUserId: any;
    var _localVideoStream: any;
    var isBlur: boolean;
}

window.vdInfo = null;
window.answerAPI = "https://cgapi.cogbee.io:8090/";
window.rAPI = "https://cgapi.cogbee.io:8083/";
window.coAPI = "https://cgapi.cogbee.io:8082/";
window.setRecordingOn = 1;


//window.answerAPI = "https://staging.cogbee.io:8090/";
//window.rAPI = "https://staging.cogbee.io:8083/";
//window.coAPI = "https://staging.cogbee.io:8082/";
////window.rAPI = "http://localhost:5055/";
//window.setRecordingOn = 0;
////alert(window.coAPI);

window.currentTimestamp = 0;
window.isBlur = false;
window._localVideoStream = undefined;
window._callAgent = undefined;
window.tokenUserId = undefined;
window.isCameraOn = "false";
window.isMicOn = "false";

loadTheme({});
initializeIcons();

const store = createStore(reducer, applyMiddleware(thunk));
const App = (): JSX.Element => {
  const [page, setPage] = useState('home');
  const [callEndReason, setCallEndReason] = useState<CallEndReason | undefined>();
  const [groupId, setGroupId] = useState('');
  const [screenWidth, setScreenWidth] = useState(0);
  const [localVideoStream, setLocalVideoStream] = useState(undefined);
  const [autoJoinCall, setAutoJoinCall] = useState(false);

  useEffect(() => {
    const setWindowWidth = (): void => {
      const width = typeof window !== 'undefined' ? window.innerWidth : 0;
      setScreenWidth(width);
    };
    setWindowWidth();
    window.addEventListener('resize', setWindowWidth);
    return (): void => window.removeEventListener('resize', setWindowWidth);
  }, []);

  const getGroupIdFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('groupId');
  };

    const getEncCodeFromUrl = (): string | null => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('q');
    };

  const getGroupId = (): string => {
    if (groupId) return groupId;
    const uriGid = getGroupIdFromUrl();
    const gid = uriGid == null || uriGid === '' ? createGUID() : uriGid;
    setGroupId(gid);
    return gid;
  };

  const getContent = (): JSX.Element => {
    if (page === 'home') {
      return (
        <HomeScreen
          startCallHandler={(): void => {
            window.history.pushState({}, document.title, window.location.href + '?groupId=' + getGroupId());
          }}
        />
      );
    } else if (page === 'configuration') {
      return (
        <ConfigurationScreen
              startCallHandler={(): void => setPage('call')}
              unsupportedStateHandler={(): void => setPage('unsupported')}
              notJoinedHandler={(): void => setPage('notjoined')}
              callEndedHandler={(errorMsg: CallEndReason): void => {
                  setCallEndReason(errorMsg);
                  setPage('error');
              }}
              groupId={getGroupId()}
              screenWidth={screenWidth}
              localVideoStream={localVideoStream}
              setLocalVideoStream={setLocalVideoStream}
              reloadPageHandler={(): void => setPage('home')}
              autoJoinCall={autoJoinCall}
        />
      );
    } else if (page === 'call') {
      return (
        <GroupCall
          endCallHandler={(): void => setPage('endCall')}
          groupId={getGroupId()}
          screenWidth={screenWidth}
          localVideoStream={localVideoStream}
          setLocalVideoStream={setLocalVideoStream}
        />
      );
    } else if (page === 'notjoined') {
        return (
            <NotJoined
                message={"If the candidate does not join the interview, you can click on the 'No Show' button provided below to close the interview."}
                joinCallHandler={(): void => { setAutoJoinCall(true); setPage('configuration');  }}
                homeHandler={(): void => setPage('unsupported')}
                screenWidth={screenWidth}
            />
        );
    } else if (page === 'endCall') {
      return (
        <EndCall
          message={'You left the call'}
          rejoinHandler={(): void => {
            window.location.reload();
          }}
          homeHandler={(): void => {
            window.location.href = window.location.href.split('?')[0];
          }}
        />
      );
    } else if (page === 'unsupported') {
      window.document.title = 'Unsupported browser';
      return (
        <>
          <a href="https://docs.microsoft.com/en-us/azure/communication-services/concepts/voice-video-calling/calling-sdk-features#calling-client-library-browser-support">
            Learn more
          </a>
          &nbsp;about browsers and platforms supported by the web calling sdk
        </>
      );
    } else if (page === 'error') {
      window.document.title = 'Call Ended';
      return (
        <div>
          <div>{`The call has ended with this error code (Code: ${callEndReason?.code} Subcode: ${callEndReason?.subCode})`}</div>

          {/*<div>*/}
          {/*  <a href="https://docs.microsoft.com/en-us/azure/communication-services/concepts/troubleshooting-info?tabs=csharp%2Cjavascript%2Cdotnet">*/}
          {/*    Learn more*/}
          {/*  </a>*/}
          {/*  &nbsp;about why this Azure Communication Services call has ended.*/}
          {/*</div>*/}
        </div>
      );
    } else if (page === 'Load') {
        window.document.title = 'COGBEE Interview';
        return (
            <div>
                {/*<div>{`Load Data`}</div>*/}
                <Spinner label="Initializing Meeting..." ariaLive="assertive" labelPosition="top" />
                {/*<div>*/}
                {/*    <Loader loaded={false} options={options} className="spinner" />*/}
                {/*    */}{/*<a href="https://docs.microsoft.com/en-us/azure/communication-services/concepts/troubleshooting-info?tabs=csharp%2Cjavascript%2Cdotnet">*/}
                {/*    */}{/*    Learn more*/}
                {/*    */}{/*</a>*/}
                {/*    */}{/*&nbsp;about why this Azure Communication Services call has ended.*/}
                {/*</div>*/}
            </div>
        );
    } else {
      return <></>;
    }
  };

    if (getGroupIdFromUrl() && page === 'home') {

        const codeQ = getEncCodeFromUrl();

        (async () => {
            //await GetInterviewDetails(codeQ, "12", "okas", "asd", 0);
            let navigator1 = window.navigator;
            navigator1 = window.navigator;
            let browserN = "";
            let versionN: number | null | false;
            versionN = 0;
            //if (window.isSecureContext === false && window.webkitRTCPeerConnection && !window.RTCIceGatherer) {
            //    browserN = 'chrome';
            //    versionN = extractVersion(navigator.userAgent, /Chrom(e|ium)\/(\d+)\./, 2);
            //} else
                if (navigator1.mediaDevices && navigator.userAgent.match(/Edge\/(\d+).(\d+)$/)) {
                browserN = 'edge';
                versionN = extractVersion(navigator.userAgent, /Edge\/(\d+).(\d+)$/, 2);
            } else if (window.RTCPeerConnection && navigator.userAgent.match(/AppleWebKit\/(\d+)\./)) {
                browserN = 'safari';
                versionN = extractVersion(navigator.userAgent, /AppleWebKit\/(\d+)\./, 1);
            } else {
                browserN = 'Not a supported browser.';
            }
            await CheckIsMobile();
            await GetInterviewDetails(codeQ, '' + versionN, navigator.userAgent, browserN);
           
            
            setPage('configuration');
           // console.log("COGBEE APPTSX Page");
        })().then(function () {
            return <Provider store={store}>{getContent()}</Provider>;
        });
        setPage('Load');
        return <Provider store={store}>{getContent()}</Provider>;
  }

  return <Provider store={store}>{getContent()}</Provider>;
};

function extractVersion(uastring: string, expr: RegExp, pos: number) {
    let match = uastring.match(expr);
    match = uastring.match(expr);
    return match && match.length >= pos && parseInt(match[pos], 10);
}

window.setTimeout(() => {
  try {
    console.log(`Azure Communication Services sample group calling app: ${lastUpdated}`);
  } catch (e) {}
}, 0);

export default App;
